import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHistory,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaImage,
  FaFilm,
  FaTimes,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import { storage } from '../../core/utils/storage';
import './SasheHistory.css';

const formatDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const SasheHistory = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [usernameFilter, setUsernameFilter] = useState('all');
  const [usernames, setUsernames] = useState([]);
  const [calendarSummary, setCalendarSummary] = useState({});
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [screenshots, setScreenshots] = useState({ files: [], gif_available: false });
  const [viewerImageIdx, setViewerImageIdx] = useState(null);
  const [showGif, setShowGif] = useState(false);

  const apiBase = (api?.defaults?.baseURL) || '';
  const token = storage.getToken ? storage.getToken() : null;

  // Build authed URL for screenshot/gif - we need to fetch via api and use blob
  const fetchAsBlobUrl = useCallback(async (url) => {
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      return URL.createObjectURL(resp.data);
    } catch (e) {
      console.error('Failed to load blob', url, e);
      return null;
    }
  }, []);

  // Fetch usernames once
  useEffect(() => {
    api.get('/sashe/history/users').then(r => setUsernames(r.data)).catch(() => {});
  }, []);

  // Fetch calendar summary for active month
  const fetchCalendar = useCallback(async () => {
    const y = activeMonth.getFullYear();
    const m = activeMonth.getMonth();
    const from = formatDate(new Date(y, m, 1));
    const to = formatDate(new Date(y, m + 1, 0));
    try {
      const r = await api.get('/sashe/history/calendar', {
        params: { from, to, user: usernameFilter }
      });
      const map = {};
      r.data.forEach(d => { map[d.date] = d; });
      setCalendarSummary(map);
    } catch (e) {
      console.error(e);
    }
  }, [activeMonth, usernameFilter]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Fetch runs for selected date
  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    setSelectedRun(null);
    setRunDetail(null);
    try {
      const dStr = formatDate(selectedDate);
      const r = await api.get('/sashe/history', {
        params: { from: dStr, to: dStr, user: usernameFilter }
      });
      setRuns(r.data);
    } catch (e) {
      toast.error('Chyba pri načítaní histórie');
    } finally {
      setLoadingRuns(false);
    }
  }, [selectedDate, usernameFilter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Load run detail + screenshots when selected
  useEffect(() => {
    if (selectedRun === null || selectedRun < 0 || selectedRun >= runs.length) {
      setRunDetail(null);
      setScreenshots({ files: [], gif_available: false });
      return;
    }
    const run = runs[selectedRun];
    if (!run) {
      setRunDetail(null);
      return;
    }

    const dStr = formatDate(selectedDate);
    api.get(`/sashe/history/${selectedRun}`, {
      params: { from: dStr, to: dStr, user: usernameFilter }
    })
      .then(r => setRunDetail(r.data))
      .catch(() => setRunDetail(null));

    if (run.screenshots && run.screenshots.run_id) {
      api.get(`/sashe/history/screenshots/${run.screenshots.run_id}`)
        .then(r => setScreenshots(r.data))
        .catch(() => setScreenshots({ files: [], gif_available: false }));
    } else {
      setScreenshots({ files: [], gif_available: false });
    }
  }, [selectedRun, runs, selectedDate, usernameFilter]);

  // Build calendar tile content (colored dots)
  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const key = formatDate(date);
    const summary = calendarSummary[key];
    if (!summary) return null;
    return (
      <div className="cal-dots">
        {summary.success > 0 && <span className="cal-dot success" title={`${summary.success} úspešných`} />}
        {summary.error > 0 && <span className="cal-dot error" title={`${summary.error} chýb`} />}
        {summary.other > 0 && <span className="cal-dot other" title={`${summary.other} ostatné`} />}
      </div>
    );
  };

  const statusBadge = (status) => {
    if (status === 'SUCCESS') return <span className="status-badge success"><FaCheckCircle /> Úspech</span>;
    if (status === 'ERROR' || status === 'FAILED') return <span className="status-badge error"><FaTimesCircle /> Chyba</span>;
    if (status === 'TIMEOUT') return <span className="status-badge error"><FaTimesCircle /> Timeout</span>;
    return <span className="status-badge other"><FaExclamationTriangle /> {status}</span>;
  };

  // Image lightbox
  const ImageViewer = ({ runId, files, currentIdx, onClose }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
      let revoked = null;
      fetchAsBlobUrl(`/sashe/history/screenshot/${runId}/${files[currentIdx]}`)
        .then(url => {
          revoked = url;
          setSrc(url);
        });
      return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [runId, files, currentIdx]);

    const prev = (e) => {
      e?.stopPropagation();
      setViewerImageIdx((currentIdx - 1 + files.length) % files.length);
    };
    const next = (e) => {
      e?.stopPropagation();
      setViewerImageIdx((currentIdx + 1) % files.length);
    };

    return (
      <div className="image-viewer-backdrop" onClick={onClose}>
        <button className="viewer-nav prev" onClick={prev} title="Predchádzajúci"><FaChevronLeft /></button>
        <button className="viewer-nav next" onClick={next} title="Ďalší"><FaChevronRight /></button>
        <button className="viewer-close" onClick={onClose}><FaTimes /></button>
        <div className="viewer-counter">{currentIdx + 1} / {files.length}</div>
        {src ? (
          <img src={src} alt={`Step ${currentIdx + 1}`} className="viewer-img" onClick={(e) => e.stopPropagation()} />
        ) : (
          <div className="viewer-loading">Načítavam...</div>
        )}
      </div>
    );
  };

  // GIF viewer
  const GifViewer = ({ runId, onClose }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
      let revoked = null;
      fetchAsBlobUrl(`/sashe/history/gif/${runId}`).then(url => { revoked = url; setSrc(url); });
      return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [runId]);
    return (
      <div className="image-viewer-backdrop" onClick={onClose}>
        <button className="viewer-close" onClick={onClose}><FaTimes /></button>
        {src ? (
          <img src={src} alt="GIF animation" className="viewer-img" onClick={(e) => e.stopPropagation()} />
        ) : <div className="viewer-loading">Načítavam GIF...</div>}
      </div>
    );
  };

  // Thumbnail component (loads via authed blob)
  const Thumbnail = ({ runId, filename, onClick }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
      let url = null;
      fetchAsBlobUrl(`/sashe/history/screenshot/${runId}/${filename}`).then(u => { url = u; setSrc(u); });
      return () => { if (url) URL.revokeObjectURL(url); };
    }, [runId, filename]);
    return (
      <div className="thumb" onClick={onClick}>
        {src ? <img src={src} alt={filename} /> : <div className="thumb-loading" />}
        <div className="thumb-label">{filename.replace('.png', '')}</div>
      </div>
    );
  };

  return (
    <div className="sashe-history-page">
      <div className="sashe-history-header">
        <h1><FaHistory /> História Sashe behov</h1>
        <div className="user-filter">
          <FaUser />
          <select value={usernameFilter} onChange={(e) => setUsernameFilter(e.target.value)}>
            <option value="all">Všetci používatelia</option>
            {usernames.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div className="sashe-history-grid">
        {/* Calendar */}
        <div className="history-calendar-panel">
          <h3>Kalendár behov</h3>
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            onActiveStartDateChange={({ activeStartDate }) => setActiveMonth(activeStartDate)}
            tileContent={tileContent}
            locale="sk-SK"
          />
          <div className="legend">
            <span><span className="cal-dot success" /> Úspech</span>
            <span><span className="cal-dot error" /> Chyba</span>
            <span><span className="cal-dot other" /> Ostatné</span>
          </div>
        </div>

        {/* Runs list */}
        <div className="history-runs-panel">
          <h3>Behy {selectedDate.toLocaleDateString('sk-SK')}</h3>
          {loadingRuns ? (
            <div className="empty-state">Načítavam...</div>
          ) : runs.length === 0 ? (
            <div className="empty-state">Žiadne behy v tento deň</div>
          ) : (
            <div className="runs-list">
              {runs.map((run, idx) => (
                <motion.div
                  key={`${run.timestamp}-${idx}`}
                  className={`run-card ${selectedRun === idx ? 'selected' : ''}`}
                  onClick={() => setSelectedRun(idx)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="run-card-header">
                    <span className="run-time">{run.time}</span>
                    {statusBadge(run.status)}
                  </div>
                  <div className="run-username"><FaUser /> {run.username}</div>
                  {run.product && <div className="run-product">{run.product}</div>}
                  <div className="run-meta">
                    {run.screenshots && (
                      <>
                        {run.screenshots.has_folder && (
                          <span className="meta-pill"><FaImage /> {run.screenshots.screenshot_count}</span>
                        )}
                        {run.screenshots.has_gif && (
                          <span className="meta-pill"><FaFilm /> GIF</span>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Run detail */}
        <div className="history-detail-panel">
          {selectedRun === null ? (
            <div className="empty-state">Vyber beh zo zoznamu</div>
          ) : (
            <>
              <h3>Detail behu</h3>
              {runDetail && (
                <>
                  <div className="detail-summary">
                    <div><strong>Čas:</strong> {runDetail.time}</div>
                    <div><strong>Používateľ:</strong> {runDetail.username}</div>
                    {runDetail.product && <div><strong>Produkt:</strong> {runDetail.product}</div>}
                    <div><strong>Stav:</strong> {statusBadge(runDetail.status)}</div>
                  </div>

                  {/* GIF + Screenshots */}
                  {runs[selectedRun]?.screenshots?.run_id && (
                    <div className="media-section">
                      {screenshots.gif_available && (
                        <button className="btn-show-gif" onClick={() => setShowGif(true)}>
                          <FaFilm /> Zobraziť GIF animáciu
                        </button>
                      )}
                      {screenshots.files.length > 0 && (
                        <>
                          <h4>Screenshoty ({screenshots.files.length})</h4>
                          <div className="thumb-grid">
                            {screenshots.files.map((f, i) => (
                              <Thumbnail
                                key={f}
                                runId={runs[selectedRun].screenshots.run_id}
                                filename={f}
                                onClick={() => setViewerImageIdx(i)}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Log */}
                  <h4>Log</h4>
                  <pre className="run-log">
                    {(runDetail.log_lines || []).join('\n')}
                  </pre>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {viewerImageIdx !== null && runs[selectedRun]?.screenshots?.run_id && (
          <ImageViewer
            runId={runs[selectedRun].screenshots.run_id}
            files={screenshots.files}
            currentIdx={viewerImageIdx}
            onClose={() => setViewerImageIdx(null)}
          />
        )}
        {showGif && runs[selectedRun]?.screenshots?.run_id && (
          <GifViewer
            runId={runs[selectedRun].screenshots.run_id}
            onClose={() => setShowGif(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SasheHistory;
